import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const API = process.env.REACT_APP_API_URL || '';

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
const CABECERAS   = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
// Índice JS (0=Dom) en orden Lun-Dom
const ORDEN_DIA   = [1, 2, 3, 4, 5, 6, 0];

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function hoy() { return new Date().toISOString().split('T')[0]; }

function mesFromFecha(fecha) {
  return fecha ? fecha.substring(0, 7) : new Date().toISOString().substring(0, 7);
}

function diasDelMes(mesStr) {
  const [year, month] = mesStr.split('-').map(Number);
  const dias = [];
  const d = new Date(year, month - 1, 1);
  while (d.getMonth() === month - 1) {
    dias.push({ fecha: d.toISOString().split('T')[0], jsDay: d.getDay() });
    d.setDate(d.getDate() + 1);
  }
  return dias;
}

function addMes(mesStr, delta) {
  const [year, month] = mesStr.split('-').map(Number);
  const d = new Date(year, month - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function AgendaCalendar({ medicoId, selectedDate, onSelectDate }) {
  const [mes, setMes]               = useState(() => mesFromFecha(selectedDate));
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [ocupacion, setOcupacion]   = useState({});
  const [extraordinaria, setExtraordinaria] = useState({});
  const [cargando, setCargando]     = useState(false);

  // Sincronizar mes con fecha seleccionada externamente (ej: usuario tipea en el campo)
  useEffect(() => {
    if (selectedDate) {
      const newMes = selectedDate.substring(0, 7);
      if (newMes !== mes) setMes(newMes);
    }
  }, [selectedDate]); // eslint-disable-line

  useEffect(() => {
    if (!medicoId) { setDisponibilidad([]); return; }
    const token = sessionStorage.getItem('token') || '';
    fetch(`${API}/api/medicos/${medicoId}/disponibilidad`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setDisponibilidad(Array.isArray(data) ? data : []))
      .catch(() => setDisponibilidad([]));
  }, [medicoId]);

  useEffect(() => {
    if (!medicoId) { setOcupacion({}); setExtraordinaria({}); return; }
    setCargando(true);
    const token = sessionStorage.getItem('token') || '';
    fetch(`${API}/api/medicos/${medicoId}/ocupacion-mes?mes=${mes}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        setOcupacion(data?.ocupacion && typeof data.ocupacion === 'object' ? data.ocupacion : {});
        setExtraordinaria(data?.extraordinaria && typeof data.extraordinaria === 'object' ? data.extraordinaria : {});
      })
      .catch(() => { setOcupacion({}); setExtraordinaria({}); })
      .finally(() => setCargando(false));
  }, [medicoId, mes]);

  if (!medicoId) {
    return (
      <Typography sx={{ fontSize: 12, color: '#546e7a', py: 1 }}>
        Seleccioná un médico para ver la agenda disponible.
      </Typography>
    );
  }

  const HOY      = hoy();
  const dias     = diasDelMes(mes);
  const diasConAgenda = new Set(disponibilidad.map(h => h.dia));

  // Offset de la primera celda: posición 0-6 (Lun=0) del primer día del mes
  const offsetInicio = ORDEN_DIA.indexOf(dias[0].jsDay);

  // Próximo día con agenda en los próximos 60 días (excluye hoy)
  let proximoDisponible = null;
  for (let i = 1; i <= 60; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const f     = d.toISOString().split('T')[0];
    const nombre = DIAS_SEMANA[d.getDay()];
    const tieneExtra = extraordinaria[f]?.length > 0;
    if ((diasConAgenda.has(nombre) || tieneExtra) && ocupacion[f] !== 'ausente') {
      proximoDisponible = f;
      break;
    }
  }

  // Días habituales (orden Lun-Dom, nombre español con tilde)
  const ORDEN_HABITUAL = ['Lunes','Martes','Miercoles','Jueves','Viernes','Sabado','Domingo'];
  const NOMBRE_DISPLAY = { Miercoles: 'Miércoles', Sabado: 'Sábado' };
  const habituales = ORDEN_HABITUAL
    .filter(d => diasConAgenda.has(d))
    .map(d => NOMBRE_DISPLAY[d] || d);

  function getCeldaStyle(fecha, jsDay) {
    // Disponibilidad extraordinaria reemplaza la agenda habitual: ese día
    // cuenta como "con agenda" aunque el día de la semana no atienda.
    const tieneAgenda   = diasConAgenda.has(DIAS_SEMANA[jsDay]) || extraordinaria[fecha]?.length > 0;
    const esAusente     = ocupacion[fecha] === 'ausente';
    const nTurnos       = typeof ocupacion[fecha] === 'number' ? ocupacion[fecha] : 0;
    const esPasado      = fecha < HOY;
    const esHoy         = fecha === HOY;
    const esSeleccionada = fecha === selectedDate;
    const interactivo   = tieneAgenda && !esAusente && !esPasado;

    let bg = 'transparent', color = '#3a4a5a', border = '1px solid transparent';
    let cursor = 'default', fontWeight = 400, opacity = 1;

    if (esPasado) {
      opacity = 0.35;
      color   = '#546e7a';
    } else if (!tieneAgenda) {
      color = '#344057';
    } else if (esAusente) {
      bg     = 'rgba(255,152,0,0.08)';
      color  = '#ff9800';
    } else {
      bg         = nTurnos === 0 ? 'rgba(102,187,106,0.12)' : 'rgba(102,187,106,0.06)';
      color      = '#66bb6a';
      cursor     = 'pointer';
      fontWeight = 500;
    }

    if (esHoy && !esSeleccionada) {
      border     = '1px solid rgba(144,164,174,0.35)';
      fontWeight = 700;
    }
    if (esSeleccionada) {
      bg         = 'rgba(30,86,160,0.3)';
      border     = '1px solid var(--c-acento)';
      color      = 'var(--c-acento)';
      fontWeight = 700;
    }

    return { bg, color, border, cursor, fontWeight, opacity, interactivo, esAusente, tieneAgenda };
  }

  const celdas = [...Array(offsetInicio).fill(null), ...dias];

  return (
    <Box sx={{ p: '10px', borderRadius: 2, bgcolor: '#080e1a', border: '1px solid #1a2640' }}>
      {/* Navegación mes */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton size="small" onClick={() => setMes(m => addMes(m, -1))}
          sx={{ color: '#546e7a', p: '2px', '&:hover': { color: 'var(--c-acento)' } }}>
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#90a4ae', minWidth: 140, textAlign: 'center' }}>
            {MESES[parseInt(mes.split('-')[1]) - 1]} {mes.split('-')[0]}
          </Typography>
          {cargando && <CircularProgress size={11} sx={{ color: '#546e7a' }} />}
        </Box>
        <IconButton size="small" onClick={() => setMes(m => addMes(m, 1))}
          sx={{ color: '#546e7a', p: '2px', '&:hover': { color: 'var(--c-acento)' } }}>
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Encabezados Lun-Dom */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: '3px' }}>
        {CABECERAS.map(c => (
          <Box key={c} sx={{ textAlign: 'center', fontSize: 10, color: '#3d566e', fontWeight: 700, py: '2px', letterSpacing: 0.3 }}>
            {c}
          </Box>
        ))}
      </Box>

      {/* Grilla de días */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {celdas.map((celda, i) => {
          if (!celda) return <Box key={`e-${i}`} />;
          const { fecha, jsDay } = celda;
          const num   = parseInt(fecha.split('-')[2]);
          const style = getCeldaStyle(fecha, jsDay);
          return (
            <Box
              key={fecha}
              onClick={() => style.interactivo && onSelectDate(fecha)}
              title={style.esAusente ? 'Ausencia registrada' : !style.tieneAgenda ? 'Sin agenda' : undefined}
              sx={{
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                fontSize: 12,
                fontWeight: style.fontWeight,
                color: style.color,
                bgcolor: style.bg,
                border: style.border,
                cursor: style.cursor,
                opacity: style.opacity,
                userSelect: 'none',
                transition: 'background 0.12s',
                '&:hover': style.interactivo
                  ? { bgcolor: 'rgba(30,86,160,0.22)', border: '1px solid rgba(30,86,160,0.5)' }
                  : {},
              }}
            >
              {num}
            </Box>
          );
        })}
      </Box>

      {/* Información contextual */}
      {disponibilidad.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {habituales.length > 0 && (
            <Typography sx={{ fontSize: 11, color: '#546e7a' }}>
              <span style={{ color: '#4a5e72' }}>Atiende:</span>{' '}
              {habituales.join(', ')}
            </Typography>
          )}
          {proximoDisponible && (
            <Typography sx={{ fontSize: 11, color: '#546e7a' }}>
              <span style={{ color: '#4a5e72' }}>Próximo disponible:</span>{' '}
              <span
                style={{ color: 'var(--c-acento)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  onSelectDate(proximoDisponible);
                  setMes(proximoDisponible.substring(0, 7));
                }}
              >
                {proximoDisponible.split('-').reverse().join('/')}
              </span>
            </Typography>
          )}
        </Box>
      )}

      {/* Leyenda */}
      <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {[
          { color: '#66bb6a', bg: 'rgba(102,187,106,0.12)', label: 'Disponible' },
          { color: '#ff9800', bg: 'rgba(255,152,0,0.08)', label: 'Ausencia' },
          { color: '#344057', bg: 'transparent', label: 'Sin agenda' },
        ].map(({ color, bg, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: bg, border: `1px solid ${color}` }} />
            <Typography sx={{ fontSize: 10, color: '#546e7a' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
